/**
 * Bolts (spec 02 §8 Lightning, §7.5 red cross), ported from rebb64: a burst lightning bubble becomes a
 * bolt in its slot (routine_3DB0 $3E2C: type $26); a player holding the red cross blows one instead of a
 * bubble (L23B8: type $44). Both run the same code each pass ($10D6; the red cross's $10D3 first toggles
 * its animation frame): 8 px and a column along, through walls, gone at column $1E or 0, and the first
 * monster in reach (the shot's own test, L105B) dies. On level 100 the boss check that replaces the
 * hole spawner (special_item_handler $1134) takes lightning bolts into the boss.
 */

import { hitBossAt } from '../monsters';
import { BOLT, GONE, QUIET_POP } from './codes';
import { killBy } from './items';
import { trappable } from './shot';
import { colOf, drawnY, endInSlot, freeBubble, freeSlot } from './slots';
import { systemOf } from './system';
import { BOLT_END_LEFT, BOLT_END_RIGHT, BOLT_STEP_PX, LEVEL_100, TRAP_RANGE } from './tuning';
import type { Bubble, Facing, PlayerId, World } from '../types';
import type { Bolt } from './state';

/**
 * $3E41: the bolt's way from the sprite frame (D_8520) of whoever burst it - frames 0-3 (facing right)
 * and 8-9 (blowing right) send it left ($84), the others right ($04): away from the popper. Burst by
 * nobody (the level-clear sweep), the C64 reads a stale D_0193 there; a slot last used by a shot blown
 * left ($80) reads the left wall's map cell, solid: right. The port always sends those right.
 */
function wayFrom(world: World, credit: PlayerId | -1): Facing {
  if (credit === -1) return 1;
  const f = world.players[credit].motion.frame;
  return f < 4 || f === 8 || f === 9 ? -1 : 1;
}

/**
 * $3E2C: the lightning bubble becomes a bolt in its slot: X snapped to its column (the C64's
 * (X - $14) & $F8 + $13 + carry), Y and row as they were; `bolt` raised.
 */
export function startBolt(world: World, b: Bubble, credit: PlayerId | -1): void {
  freeBubble(world, b);
  const x = ((((b.x - 0x14) & 0xff) & 0xf8) + (b.x >= 0x14 ? 0x14 : 0x13)) & 0xff;
  systemOf(world).bolts.push({
    slot: b.motion.slot, x, y: b.y, col: colOf(b), row: b.motion.row, subY: b.motion.subY,
    dir: wayFrom(world, credit), player: credit, redCross: false,
  });
  world.events.push({ type: 'bolt', player: credit });
}

/**
 * L23B8: a red-cross shot where the bubble would have been - the blow's X (+8 facing right), Y (+8 mid
 * jump), column and row - flying the player's way, in the highest free slot. The caller has checked a
 * slot is free and Y is not above $15 (else the C64 gives up before the red cross is counted).
 */
export function fireRedCross(world: World, id: PlayerId, x: number, y: number, dir: Facing): void {
  const dy = (y - 0x15) & 0xff;
  systemOf(world).bolts.push({
    slot: freeSlot(world), x: x & 0xff, y: y & 0xff, col: ((x - 0x14) & 0xff) >> 3, row: dy >> 3, subY: dy & 6,
    dir, player: id, redCross: true,
  });
  world.events.push({ type: 'bolt', player: id });
}

/**
 * $10D6, one pass of a bolt (a lightning bolt waits out the clock's freeze - L0D4E skips types $24-$33
 * while $67 counts - a red-cross shot, type $44, does not). Moving right it is gone on reaching column
 * $1E, left column 0, without a test there; else the first monster from the last slot within |dx| < 16,
 * |dy| < 16 of its X, Y dies (one a pass), flying off as a purple diamond.
 */
export function boltStep(world: World, bolt: Bolt): void {
  if (!bolt.redCross && world.power.clock > 0) return;
  bolt.x = (bolt.x + bolt.dir * BOLT_STEP_PX) & 0xff;
  bolt.col += bolt.dir;
  // The C64 tests for equality on its 8-bit column; no bolt starts past either end, so >= / <= is the same.
  if (bolt.dir > 0 ? bolt.col >= BOLT_END_RIGHT : bolt.col <= BOLT_END_LEFT) return endBolt(world, bolt, GONE, BOLT);
  for (let i = world.monsters.length - 1; i >= 0; i--) {
    const mon = world.monsters[i];
    if (!mon || !trappable(mon)) continue;
    if (Math.abs(bolt.x - mon.x) >= TRAP_RANGE || Math.abs(bolt.y - mon.y) >= TRAP_RANGE) continue;
    killBy(world, mon.id, bolt.redCross ? 'other' : 'lightning');
    return;
  }
}

/** The bolt leaves its list for its slot's vanish ($38) or a burst ($3A). */
function endBolt(world: World, bolt: Bolt, code: number, saved: number): void {
  const sys = systemOf(world);
  sys.bolts.splice(sys.bolts.indexOf(bolt), 1);
  endInSlot(world, bolt.slot, { x: bolt.x, y: bolt.y, row: bolt.row, subY: bolt.subY }, code, saved);
}

/** $171B: the level-clear sweep bursts every lightning bolt; a red-cross shot ($44) flies on. */
export function burstBolts(world: World): void {
  for (const bolt of systemOf(world).bolts.slice()) if (!bolt.redCross) endBolt(world, bolt, QUIET_POP, BOLT);
}

/**
 * $1134 (level 100's replacement for the hole spawner, once a pass before the bursting pass): each
 * lightning bolt, at its column and row (col x 8 + $14, row x 8 + $15), is offered to the boss
 * (`hitBossAt`, the MONSTER stream's); a hit bursts it ($3A).
 */
export function boltsHitBoss(world: World): void {
  if (world.levelIndex !== LEVEL_100) return;
  for (const bolt of systemOf(world).bolts.slice()) {
    if (bolt.redCross) continue;
    if (hitBossAt(world, (bolt.col * 8 + 0x14) & 0xff, (bolt.row * 8 + 0x15) & 0xff)) {
      endBolt(world, bolt, QUIET_POP, QUIET_POP);
    }
  }
}

/** What the renderer draws of the bolts (see `boltLooks` in index.ts): where the C64 draws them. */
export function boltView(world: World): { x: number; y: number; dir: Facing; player: PlayerId | -1; redCross: boolean }[] {
  return systemOf(world).bolts.map((b) => ({
    x: (b.col * 8 + 0x14) & 0xff, y: drawnY(b.row, b.subY), dir: b.dir, player: b.player, redCross: b.redCross,
  }));
}
